import * as React from 'react'

import { styled, mq, spacing, Text } from '@thg-commerce/gravity-theme'
import {
  Image,
  SafeHtml,
  ProductImageUrls,
} from '@thg-commerce/gravity-elements'
import {
  ProductButton,
  ProductButtonProps,
} from '@thg-commerce/enterprise-components'

export interface ProductGridProps {
  products: ProductGridProductProps[]
  isAmp: boolean
}

export interface ProductGridProductProps {
  sku: string
  title: string
  url: string
  image: {
    urls: ProductImageUrls
    lazy?: boolean
  }
  price: string
  button: Omit<ProductButtonProps, 'href' | 'productSku'>
  description: string
}

interface ProductGridItemProps {
  isLastOddChild: boolean
  hasNoBottomBorder: boolean
}

const StyledImage = styled(Image)`
  background-color: transparent;
`

const ProductGridWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-top: 1px solid ${(props) => props.theme.colors.palette.greys.light};
  border-left: 1px solid ${(props) => props.theme.colors.palette.greys.light};
`

const ProductDetails = styled.div`
  flex-shrink: 1;
`

const ProductGridItem = styled.div<ProductGridItemProps>`
  display: flex;
  flex-direction: column;
  flex-basis: 100%;
  border-bottom: 1px solid ${(props) => props.theme.colors.palette.greys.light};
  border-right: 1px solid ${(props) => props.theme.colors.palette.greys.light};
  padding: ${spacing(4)};

  ${StyledImage} {
    max-width: 196px;
    width: 100%;
    margin-bottom: ${spacing(3)};
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    flex-basis: 50%;
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    flex-basis: calc(100% / 3);
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
    flex-basis: 25%;

    &:nth-child(4n + 2),
    &:nth-child(4n + 3) {
      flex-direction: row;
      flex-basis: 75%;

      ${StyledImage} {
        max-width: 481px;
        width: 100%;
        margin-bottom: 0;
        margin-right: ${spacing(4)};
      }

      ${ProductDetails} {
        max-width: 45%;
      }
    }
  }
`

const ProductTitle = styled.h3`
  ${Text('medium1', 'alternate')};
  margin-bottom: ${spacing(2)};
`

const PriceWrapper = styled(SafeHtml)`
  ${Text('medium1', 'default')};
  margin-bottom: ${spacing(2)};
`

const StyledProductButton = styled(ProductButton)`
  margin-top: ${spacing(3)};
`

export const ProductGrid = React.memo((props: ProductGridProps) => {
  const totalProducts = props.products.length
  const items = props.products.map(
    (product: ProductGridProductProps, index) => {
      const imgSrc = [
        {
          url: product.image.urls.largeProduct,
          width: 960,
        },
      ]
      product.image.urls.thumbnail &&
        imgSrc.push({
          url: product.image.urls.thumbnail,
          width: 130,
        })
      product.image.urls.zoom &&
        imgSrc.push({
          url: product.image.urls.zoom,
          width: 1600,
        })
      const lastProduct = totalProducts - 1 === index
      const isOddIndex = (index + 1) % 2 === 1

      return (
        <ProductGridItem
          key={index}
          isLastOddChild={lastProduct && isOddIndex}
          hasNoBottomBorder={
            (index + 1) % 2 === 0 && index === totalProducts - 2
          }
        >
          <StyledImage
            src={imgSrc}
            alt=""
            width="100%"
            height="auto"
            isAmp={props.isAmp}
            lazy
          />
          <ProductDetails>
            <ProductTitle>{product.title}</ProductTitle>
            <PriceWrapper content={product.price} />
            <SafeHtml content={product.description} />
            <StyledProductButton
              title={product.button.title}
              quickBuyTitle={product.button.quickBuyTitle}
              href={product.url}
              ariaLabel={product.button.ariaLabel}
              productSku={product.sku}
            />
          </ProductDetails>
        </ProductGridItem>
      )
    },
  )
  return <ProductGridWrapper>{items}</ProductGridWrapper>
})
